/**
 * Shared hook for PracticeScreen data and handlers.
 * Used by both mobile and web layouts.
 */

import { useCallback, useMemo } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useLessonStore } from '../../store/lessonStore';
import {
  selectLessonPath,
  selectSelectedLevel,
  selectIsLoadingPath,
  selectPathError,
  selectFetchLessonPath,
  selectSetSelectedLevel,
} from '../../store/lessonSelectors';
import type { LessonPathItem, LessonPathSection, CEFRLevel } from '../../types/lesson';

export interface PathSectionData extends LessonPathSection {
  data: LessonPathItem[];
}

export function getLessonState(
  lesson: LessonPathItem,
  sectionLessons: LessonPathItem[],
  index: number,
): 'completed' | 'next' | 'available' {
  if (lesson.completion) {
    return 'completed';
  }
  const firstUncompletedIndex = sectionLessons.findIndex((l) => !l.completion);
  if (index === firstUncompletedIndex) return 'next';
  return 'available';
}

export function usePracticeData(onLessonSelect?: (slug: string) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation: any = useNavigation();
  const lessonPath = useLessonStore(selectLessonPath);
  const selectedLevel = useLessonStore(selectSelectedLevel);
  const isLoadingPath = useLessonStore(selectIsLoadingPath);
  const pathError = useLessonStore(selectPathError);
  const fetchLessonPath = useLessonStore(selectFetchLessonPath);
  const setSelectedLevel = useLessonStore(selectSetSelectedLevel);

  useFocusEffect(
    useCallback(() => {
      const level = useLessonStore.getState().selectedLevel;
      fetchLessonPath(level);
    }, [fetchLessonPath])
  );

  const handleLevelPress = useCallback(
    (level: CEFRLevel) => {
      setSelectedLevel(level);
    },
    [setSelectedLevel],
  );

  const handleLessonPress = useCallback(
    (slug: string, title?: string) => {
      if (onLessonSelect) {
        onLessonSelect(slug);
      } else {
        navigation.navigate('LessonDetail', { slug, title });
      }
    },
    [navigation, onLessonSelect],
  );

  const handleContinuePress = useCallback(
    (slug: string, title?: string) => {
      if (onLessonSelect) {
        onLessonSelect(slug);
      } else {
        navigation.navigate('LessonDetail', { slug, title });
      }
    },
    [navigation, onLessonSelect],
  );

  const onRetry = useCallback(() => {
    const level = useLessonStore.getState().selectedLevel;
    fetchLessonPath(level);
  }, [fetchLessonPath]);

  const sections: PathSectionData[] = useMemo(() => {
    if (!lessonPath) return [];
    return lessonPath.sections.map((s) => ({ ...s, data: s.lessons }));
  }, [lessonPath]);

  return {
    lessonPath,
    selectedLevel,
    isLoadingPath,
    pathError,
    sections,
    handleLevelPress,
    handleLessonPress,
    handleContinuePress,
    onRetry,
  };
}
